import { useState, useEffect, useRef } from 'react';
import type { PunchResponse } from '@src/api/punchTypes';
import { usePunchFlow } from '@src/context/PunchFlowContext';
import { useIdleTimeout } from '@src/hooks/useIdleTimeout';
import { TopBar } from '@src/components/TopBar';
import { LeftPanel } from './LeftPanel';
import { ShiftPanel } from './ShiftPanel';
import { InactivityModal } from './InactivityModal';
import './UserStatusPage.css';

const IDLE_TIMEOUT_MS = 60_000;

export function UserStatusPage() {
    const { state, lastStatusRef, dispatch, actionContextRef } = usePunchFlow();
    const [loadingLabel, setLoadingLabel] = useState<string | null>(null);
    const submitted = loadingLabel !== null;

    const [showInactivityModal, setShowInactivityModal] = useState(false);

    // A modal from the punch flow is open when phase is not showing_status.
    // We use a ref so the idle callback always reads the latest value.
    const isFlowModalOpenRef = useRef(false);
    isFlowModalOpenRef.current = state.phase !== 'showing_status';

    const showInactivityModalRef = useRef(showInactivityModal);
    showInactivityModalRef.current = showInactivityModal;

    const { reset: resetIdleTimer } = useIdleTimeout(IDLE_TIMEOUT_MS, () => {
        if (isFlowModalOpenRef.current || showInactivityModalRef.current) {
            // A modal is already open — restart the timer rather than interrupting.
            resetIdleTimer();
        } else {
            setShowInactivityModal(true);
        }
    });

    const response =
        state.phase === 'showing_status'
            ? state.response
            : lastStatusRef.current;

    // Reset the submission guard whenever we return to the showing_status phase
    // (covers both fresh PIN entry and returning from a dismissed intermediate modal).
    useEffect(() => {
        if (state.phase === 'showing_status') {
            setLoadingLabel(null);
        }
    }, [state.phase]);

    function handleAction(
        execute: () => Promise<PunchResponse>,
        label: string
    ) {
        if (submitted) return;
        setLoadingLabel(label);
        actionContextRef.current = {
            user: response!.user,
            actionLabel: label
        };
        dispatch({ type: 'ACTION_SELECTED', execute });
    }

    function handleStayActive() {
        setShowInactivityModal(false);
        resetIdleTimer();
    }

    function handleSignOut() {
        setShowInactivityModal(false);
        dispatch({ type: 'DISMISS' });
    }

    return (
        <div data-testid="user-status-page" className="user-status-page">
            <TopBar
                statusMode
                onStatusDismiss={() => dispatch({ type: 'DISMISS' })}
            />
            <div className="user-status-page__content">
                {response ? (
                    <>
                        <div className="user-status-page__left-panel">
                            <LeftPanel response={response} />
                        </div>
                        <div className="user-status-page__right-panel">
                            <ShiftPanel
                                response={response}
                                loadingLabel={loadingLabel}
                                onAction={handleAction}
                            />
                        </div>
                    </>
                ) : null}
            </div>
            {showInactivityModal && (
                <InactivityModal
                    onStayActive={handleStayActive}
                    onSignOut={handleSignOut}
                />
            )}
        </div>
    );
}
